/**
 * GalleryV2Content
 *
 * Hauptinhalt der Gallery mit Grid/List/Bento Views.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useGallerySearch, useFavorites } from '@/hooks/useGalleryV2';
import { useImageActions } from '@/hooks/useImageActions';
import { useApprovalActions } from '@/hooks/useApproval';
import { GalleryV2Toolbar } from './GalleryV2Toolbar';
import { GalleryGridView } from './views/GalleryGridView';
import { GalleryListView } from './views/GalleryListView';
import { GalleryBentoView } from './views/GalleryBentoView';
import { SelectionActionBar } from '@/components/v2/tools/gallery/SelectionActionBar';
import { GalleryAddToProductBridge } from '@/components/v2/tools/gallery/GalleryAddToProductBridge';
import { useUnifiedAddToProductFlag } from '@/hooks/useUnifiedAddToProductFlag';
import { EmptyState, Spinner } from '@/components/v2/ui';
import { Images } from 'lucide-react';
import type { ViewMode } from './GalleryV2Layout';
import type { GalleryImage } from '@/lib/api/gallery-v2';

interface GalleryV2ContentProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onImageClick?: (image: GalleryImage) => void;
  isApprovalMode?: boolean;
}

export function GalleryV2Content({
  viewMode,
  onViewModeChange,
  selectedIds,
  onSelectionChange,
  sidebarOpen,
  onToggleSidebar,
  onImageClick,
  isApprovalMode = false,
}: GalleryV2ContentProps) {
  const { t } = useTranslation();
  const { params, images, pagination, isLoading, updateParams, refetch } = useGallerySearch();
  const { toggle: toggleFavorite } = useFavorites();
  const { downloadImage, deleteImage } = useImageActions();
  const { bulkUpdate, isUpdatingBulk } = useApprovalActions();
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  // Add-to-product (flag-gated): multi-select action only offered when the new
  // unified overlay is enabled. Legacy path has no multi-image surface.
  const addToProductEnabled = useUnifiedAddToProductFlag();
  const [addToProductOpen, setAddToProductOpen] = useState(false);

  const handleToggleSelect = useCallback(
    (imageId: string) => {
      const newIds = new Set(selectedIds);
      if (newIds.has(imageId)) {
        newIds.delete(imageId);
      } else {
        newIds.add(imageId);
      }
      onSelectionChange(newIds);
    },
    [selectedIds, onSelectionChange]
  );

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === images.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(images.map((img) => img.id)));
    }
  }, [images, selectedIds, onSelectionChange]);

  const handlePageChange = useCallback(
    (page: number) => {
      updateParams({ page });
      onSelectionChange(new Set());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [updateParams, onSelectionChange]
  );

  const handlePerPageChange = useCallback(
    (n: number) => {
      updateParams({ per_page: n, page: 1 });
      onSelectionChange(new Set());
    },
    [updateParams, onSelectionChange]
  );

  const handleToggleFavorite = useCallback(
    async (imageId: string) => {
      await toggleFavorite(imageId);
    },
    [toggleFavorite]
  );

  const handleDeselectAll = useCallback(() => {
    onSelectionChange(new Set());
  }, [onSelectionChange]);

  // Bulk Download
  const handleBulkDownload = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setIsBulkDownloading(true);
    try {
      for (const imageId of selectedIds) {
        await downloadImage(imageId);
      }
      toast.success(t('toast.success.imagesDownloaded', { count: selectedIds.size }));
    } catch {
      toast.error(t('toast.error.downloading'));
    } finally {
      setIsBulkDownloading(false);
    }
  }, [selectedIds, downloadImage, t]);

  // Bulk Delete
  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const msg = selectedIds.size === 1
      ? t('gallery.deleteConfirmSingle')
      : t('gallery.deleteConfirmMultiple', { count: selectedIds.size });
    if (!window.confirm(msg)) return;

    setIsBulkDeleting(true);
    try {
      for (const imageId of selectedIds) {
        await deleteImage(imageId);
      }
      toast.success(t('toast.success.imagesDeleted', { count: selectedIds.size }));
      onSelectionChange(new Set());
      refetch();
    } catch {
      toast.error(t('toast.error.deleting'));
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedIds, deleteImage, onSelectionChange, refetch, t]);

  // Bulk Approve
  const handleBulkApprove = useCallback(async () => {
    if (selectedIds.size === 0) return;
    bulkUpdate({ imageIds: Array.from(selectedIds), action: 'approve' }, {
      onSuccess: () => {
        onSelectionChange(new Set());
      },
    });
  }, [selectedIds, bulkUpdate, onSelectionChange]);

  // Bulk Reject
  const handleBulkReject = useCallback(async () => {
    if (selectedIds.size === 0) return;
    bulkUpdate({ imageIds: Array.from(selectedIds), action: 'reject' }, {
      onSuccess: () => {
        onSelectionChange(new Set());
      },
    });
  }, [selectedIds, bulkUpdate, onSelectionChange]);

  // View Props
  const viewProps = {
    images,
    selectedIds,
    onToggleSelect: handleToggleSelect,
    onToggleFavorite: handleToggleFavorite,
    onImageClick,
    showApprovalActions: isApprovalMode,
  };

  return (
    <>
      <GalleryV2Toolbar
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        selectedCount={selectedIds.size}
        selectedIds={selectedIds}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={onToggleSidebar}
        onClearSelection={() => onSelectionChange(new Set())}
        perPage={params.per_page ?? 24}
        onPerPageChange={handlePerPageChange}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : images.length === 0 ? (
          <EmptyState
            icon={<Images className="h-12 w-12" />}
            title={t('gallery.noImages')}
            description="Passe deine Filter an oder generiere neue Bilder."
          />
        ) : (
          <>
            {(viewMode === 'grid' || viewMode === 'grid5') && (
              <GalleryGridView {...viewProps} columns={viewMode === 'grid5' ? 5 : 4} />
            )}
            {viewMode === 'list' && <GalleryListView {...viewProps} />}
            {viewMode === 'bento' && <GalleryBentoView {...viewProps} />}

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-4 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  {t('common.back')}
                </button>
                <span className="px-4 py-2 text-sm text-neutral-600 dark:text-neutral-400">
                  Seite {pagination.page} von {pagination.total_pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                  className="px-4 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  {t('common.next')}
                </button>
              </div>
            )}

            {/* Total Count */}
            {pagination && (
              <p className="mt-4 text-center text-sm text-neutral-500">
                {pagination.total_images} Bilder insgesamt
              </p>
            )}
          </>
        )}
      </div>

      {/* Selection Action Bar */}
      <SelectionActionBar
        selectedCount={selectedIds.size}
        totalCount={images.length}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
        onDownload={handleBulkDownload}
        onDelete={handleBulkDelete}
        isDownloading={isBulkDownloading}
        isDeleting={isBulkDeleting}
        onApprove={isApprovalMode ? handleBulkApprove : undefined}
        onReject={isApprovalMode ? handleBulkReject : undefined}
        isApprovingBulk={isUpdatingBulk}
        isRejectingBulk={isUpdatingBulk}
        onAddToProduct={
          addToProductEnabled ? () => setAddToProductOpen(true) : undefined
        }
      />

      {/* Add-to-product overlay (flag-gated; multi-select source set) */}
      <GalleryAddToProductBridge
        open={addToProductOpen}
        imageIds={[...selectedIds]}
        onClose={() => setAddToProductOpen(false)}
        onComplete={() => {
          onSelectionChange(new Set());
          setAddToProductOpen(false);
        }}
      />
    </>
  );
}
